import React from 'react';
import { makeStyles } from '@material-ui/core';

import { Collapse, Link, units, Grid } from '@ha/dsl';
import { useIntl } from 'ha/i18n';
import {
  PropertySizeFilterValue,
  PropertySizeFilterCount,
  PropertySizeFilterStats,
} from 'ha/types/SearchFilters';

import CommonRadioFilter from './CommonRadioFilter';
import CommonCheckboxTitleWithCount from './CommonCheckboxTitleWithCount';
import CommonMinMaxNumericInput from './CommonMinMaxNumericInput';

interface Props {
  value?: PropertySizeFilterValue;
  onChange: (value: PropertySizeFilterValue) => void;
  count?: PropertySizeFilterCount;
  stats?: PropertySizeFilterStats;
}

const useStyles = makeStyles(() => ({
  link: {
    display: 'inline-block',
    fontSize: units.rem(0.875),
    marginTop: units.rem(0.5),
  },
  inputContainer: {
    paddingBottom: units.rem(0.875),
  },
  clearLinkContainer: {
    marginBottom: units.rem(0.5),
  },
  clearLink: {
    fontSize: units.rem(0.875),
  },
}));

interface InputValue {
  min: string;
  max: string;
}

const PropertyTypeFilter: React.FC<Props> = ({
  value,
  onChange,
  count,
  stats,
}) => {
  const classes = useStyles();
  const { T } = useIntl();

  const [showMinMaxInput, setShowMinMaxInput] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<InputValue>({
    min: '0',
    max: '0',
  });

  const options = [
    {
      value: '15',
      title: (
        <CommonCheckboxTitleWithCount
          title={`15 m² ${T('option.or_more')}`}
          count={count?.over15}
        />
      ),
      testLocator: 'Filter 15 m² or more',
    },
    {
      value: '30',
      title: (
        <CommonCheckboxTitleWithCount
          title={`30 m² ${T('option.or_more')}`}
          count={count?.over30}
        />
      ),
      testLocator: 'Filter 30 m² or more',
    },
    {
      value: '60',
      title: (
        <CommonCheckboxTitleWithCount
          title={`60 m² ${T('option.or_more')}`}
          count={count?.over60}
        />
      ),
      testLocator: 'Filter 60 m² or more',
    },
    {
      value: '90',
      title: (
        <CommonCheckboxTitleWithCount
          title={`90 m² ${T('option.or_more')}`}
          count={count?.over90}
        />
      ),
      testLocator: 'Filter 90 m² or more',
    },
  ];

  React.useEffect(() => {
    setInputValue({
      min: String(value?.sizeMin || 0),
      max: String(value?.sizeMax || stats?.maxValue || 0),
    });

    if (
      Boolean(value?.sizeMax) ||
      (Boolean(value?.sizeMin) &&
        !options.find(item => item.value === String(value?.sizeMin)))
    ) {
      setShowMinMaxInput(true);
    }
  }, [value, stats?.maxValue]);

  const onSelect = React.useCallback(
    minSize => {
      onChange({
        sizeMin: parseInt(minSize, 10),
      });
    },
    [onChange],
  );

  const handleChange = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    setShowMinMaxInput(prev => !prev);
  }, []);

  const onBlur = React.useCallback(() => {
    const inputTmpValue = {
      min: inputValue.min,
      max: inputValue.max,
    };

    if (
      inputValue.min === '' ||
      Number(inputValue.min) < 0 ||
      Number(inputValue.min) > Number(inputValue.max) ||
      Number(inputValue.min) > (stats?.maxValue || 0)
    ) {
      inputTmpValue.min = '0';
      setInputValue(inputTmpValue);
    }

    if (
      inputValue.max === '' ||
      Number(inputValue.max) < 0 ||
      Number(inputValue.max) < Number(inputValue.min) ||
      Number(inputValue.max) > (stats?.maxValue || 0)
    ) {
      inputTmpValue.max = String(stats?.maxValue);
      setInputValue(inputTmpValue);
    }

    if (
      !value?.sizeMin &&
      !value?.sizeMax &&
      inputTmpValue.min === '0' &&
      parseInt(inputTmpValue.max, 10) === stats?.maxValue
    ) {
      return;
    }

    onChange({
      ...(inputTmpValue.min !== '0' && {
        sizeMin: parseInt(inputTmpValue.min, 10),
      }),
      ...(parseInt(inputTmpValue.max, 10) !== stats?.maxValue && {
        sizeMax: parseInt(inputTmpValue.max, 10),
      }),
    });
  }, [onChange, inputValue, stats?.maxValue, value]);

  const onMinChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue({
        min: e.target.value,
        max: inputValue.max,
      });
    },
    [inputValue.max],
  );

  const onMaxChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue({
        min: inputValue.min,
        max: e.target.value,
      });
    },
    [inputValue.min],
  );

  const radioValue = React.useMemo(
    () =>
      Boolean(value?.sizeMin) &&
      !value?.sizeMax &&
      options.find(item => item.value === String(value?.sizeMin))
        ? `${value?.sizeMin}`
        : null,
    [value, options],
  );

  return (
    <Grid container direction="column">
      <Grid item>
        <CommonRadioFilter
          name={T('Recently added listings')}
          value={radioValue}
          onChange={onSelect}
          options={options}
        />
      </Grid>
      <Grid item>
        <Link
          to=""
          className={classes.link}
          kind="underline"
          onClick={handleChange}
        >
          {`${showMinMaxInput ? '-' : '+'} ${T(
            'search.filters.property_size.specify.link',
          )}`}
        </Link>
      </Grid>
      <Grid item className={classes.inputContainer}>
        <Collapse in={showMinMaxInput}>
          <CommonMinMaxNumericInput
            id="propertySizeFilter"
            labelMin={T('search.filters.property_size.specify.label_min')}
            valueMin={inputValue.min}
            onMinChange={onMinChange}
            onMinBlur={onBlur}
            labelMax={T('search.filters.property_size.specify.label_max')}
            valueMax={inputValue.max}
            onMaxChange={onMaxChange}
            onMaxBlur={onBlur}
            suffix="m²"
          />
        </Collapse>
      </Grid>
    </Grid>
  );
};

export default PropertyTypeFilter;
